import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Spacings } from '@styles'

import { DrawerBodyWrapper, DrawerFooterWrapper, Tabs } from '@base'
import { CRAN_SECTION_TYPES, strings } from '@constants'
import { CommentsSection, DrawerNavigationWrapper } from '../styles'
import CranCommentCard from '../Components/CranCommentCard'
import { useForceUpdate } from '@hooks'
import { showLoader } from '@data/state/action'
import { addCommentInCranReport } from '@data/conn/post/cranReport'
import { getCranReportById } from '@data/conn/get/cranReport'
import { getLatestCommentImagePaths } from '../../../cranCommentImages'

// Grey body so the white comment cards stand out against it.
const DrawerBody = styled(DrawerBodyWrapper)`
	background: ${({ theme }) => theme.colors.surface.page};
`

// Each comment sits in its own card, spaced apart.
const FieldWrapper = styled.div`
	& + & {
		margin-top: ${Spacings.SPACING_3B};
	}
`

const dataToForm = (prevState, data) => {
	if (data === null) {
		return prevState
	}
	let _obj = data?.slice(-1)?.[0]
	return {
		...prevState,
		value: _obj?.comment,
		show_in_cran: _obj?.show_in_cran,
		comment_type: _obj?.comment_type,
		// Documents of the same (last) entry the comment itself comes from.
		documents: getLatestCommentImagePaths(data),
		dirty: true,
	}
}

const deriveFormState = (data) => {
	const _state = {
		brief_company_profile: {
			label: 'Brief profile of company',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		competitive_position: {
			label: 'Competitive Position',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		diversification: {
			label: 'Diversification',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		assets_quality: {
			label: 'Quality of Assets',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		project_risk: {
			label: 'Project Risk',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		industry_diversification: {
			label: 'Industry Diversification',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		parent_and_group_companies: {
			label: 'Parent companies and group companies',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		support_level: {
			label: 'Support Level',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		strategic: {
			label: 'Strategic',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		business: {
			label: 'Business',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		present_bankers: {
			label: 'Present Bankers (loan or faculty avaied)',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		market_conduct: {
			label: 'Conduct (Market feedback if any)',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		growth_prospects: {
			label: 'Growth Prospects',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		cyclicality: {
			label: 'Cyclicality',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		competitive_intensity: {
			label: 'Competitive Intensity',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		regulatory_risk: {
			label: 'Regulatory Risk',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		fund_based_credit_rating: {
			label: 'Fund Based Credit Rating',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		non_fund_based_credit_rating: {
			label: 'Non Fund Based Credit Rating',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		execution_manufacturing_capabilities: {
			label: 'Execution / Manufacturing Capabilities',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		project_product_portfolio: {
			label: 'Project / Product Portfolio',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		key_industries_clients_catered: {
			label: 'Key Industries Clients Catered',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
	}

	if (data) {
		_state.brief_company_profile = dataToForm(_state.brief_company_profile, data?.brief_company_profile)
		_state.competitive_position = dataToForm(_state.competitive_position, data?.competitive_position)
		_state.diversification = dataToForm(_state.diversification, data?.diversification)
		_state.assets_quality = dataToForm(_state.assets_quality, data?.assets_quality)
		_state.project_risk = dataToForm(_state.project_risk, data?.project_risk)
		_state.industry_diversification = dataToForm(_state.industry_diversification, data?.industry_diversification)
		_state.parent_and_group_companies = dataToForm(_state.parent_and_group_companies, data?.parent_and_group_companies)
		_state.support_level = dataToForm(_state.support_level, data?.support_level)
		_state.strategic = dataToForm(_state.strategic, data?.strategic)
		_state.business = dataToForm(_state.business, data?.business)
		_state.present_bankers = dataToForm(_state.present_bankers, data?.present_bankers)
		_state.market_conduct = dataToForm(_state.market_conduct, data?.market_conduct)
		_state.growth_prospects = dataToForm(_state.growth_prospects, data?.growth_prospects)
		_state.cyclicality = dataToForm(_state.cyclicality, data?.cyclicality)
		_state.competitive_intensity = dataToForm(_state.competitive_intensity, data?.competitive_intensity)
		_state.regulatory_risk = dataToForm(_state.regulatory_risk, data?.regulatory_risk)
		_state.fund_based_credit_rating = dataToForm(_state.fund_based_credit_rating, data?.fund_based_credit_rating)
		_state.non_fund_based_credit_rating = dataToForm(_state.non_fund_based_credit_rating, data?.non_fund_based_credit_rating)
		_state.execution_manufacturing_capabilities = dataToForm(
			_state.execution_manufacturing_capabilities,
			data?.execution_manufacturing_capabilities
		)
		_state.project_product_portfolio = dataToForm(_state.project_product_portfolio, data?.project_product_portfolio)
		_state.key_industries_clients_catered = dataToForm(_state.key_industries_clients_catered, data?.key_industries_clients_catered)
	}
	return _state
}

const CompanyRiskForm = ({ probeId }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()
	const [selectedTab, setSelectedTab] = useState(0)

	useEffect(() => {
		dispatch(showLoader(true))
		getCranReportById(probeId, [CRAN_SECTION_TYPES.ABOUT_COMPANY])
			.then((response) => {
				setFormState(deriveFormState(response))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [forceUpdate])

	const tabs = useMemo(() => {
		return [
			{
				label: 'Brief',
				selected: selectedTab === 0,
				onClick: () => setSelectedTab(0),
			},
			{
				label: strings('business'),
				selected: selectedTab === 1,
				onClick: () => setSelectedTab(1),
			},
			{
				label: strings('industry'),
				selected: selectedTab === 2,
				onClick: () => setSelectedTab(2),
			},
		]
	}, [probeId, selectedTab])
	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	// Every change to a comment — its text, its CRAN visibility or its sentiment — is saved with
	// the same endpoint, so the card never holds an unsaved state.
	const saveComment = (key, overrides = {}) => {
		const obj = { ...formState[key], ...overrides }
		const comment = {
			comment: obj.value,
			comment_type: obj.comment_type,
			created_by: loggedInUser?.user_id,
			show_in_cran: obj?.show_in_cran,
		}
		dispatch(showLoader(true))
		addCommentInCranReport({
			userId: loggedInUser?.user_id,
			commentValue: comment,
			commentKey: key,
			probe42Id: probeId,
		})
			.then((response) => {})
			.catch((error) => {})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const updateCheckHandler = (key) => (val) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				show_in_cran: val,
			},
		}))
		saveComment(key, { show_in_cran: val })
	}

	const commentTypeHandler = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				comment_type: value,
			},
		}))
		saveComment(key, { comment_type: value })
	}

	// `value` is the draft the editor just saved; it is also written back to the form so the card
	// shows the new comment immediately, before the refetch lands.
	const addHandler = (key) => (value) => {
		const comment = value ?? formState[key]?.value
		updateState(key)(comment)
		saveComment(key, { value: comment })
	}

	const getBriefComments = () => {
		if (selectedTab === 0)
			return (
				<>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.brief_company_profile?.label}
							placeholder={formState?.brief_company_profile?.placeholder}
							toggleState={formState?.brief_company_profile?.comment_type}
							savedDocuments={formState?.brief_company_profile?.documents}
							textValue={formState?.brief_company_profile?.value}
							onCheckHandler={updateCheckHandler('brief_company_profile')}
							checkValue={formState?.brief_company_profile?.show_in_cran}
							onToggleChange={commentTypeHandler('brief_company_profile')}
							onButtonClick={addHandler('brief_company_profile')}
							disabled={formState?.brief_company_profile?.disabled}
							probeId={probeId}
							commentKey={'brief_company_profile'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.execution_manufacturing_capabilities?.label}
							placeholder={formState?.execution_manufacturing_capabilities?.placeholder}
							toggleState={formState?.execution_manufacturing_capabilities?.comment_type}
							savedDocuments={formState?.execution_manufacturing_capabilities?.documents}
							textValue={formState?.execution_manufacturing_capabilities?.value}
							onCheckHandler={updateCheckHandler('execution_manufacturing_capabilities')}
							checkValue={formState?.execution_manufacturing_capabilities?.show_in_cran}
							onToggleChange={commentTypeHandler('execution_manufacturing_capabilities')}
							onButtonClick={addHandler('execution_manufacturing_capabilities')}
							disabled={formState?.execution_manufacturing_capabilities?.disabled}
							probeId={probeId}
							commentKey={'execution_manufacturing_capabilities'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.project_product_portfolio?.label}
							placeholder={formState?.project_product_portfolio?.placeholder}
							toggleState={formState?.project_product_portfolio?.comment_type}
							savedDocuments={formState?.project_product_portfolio?.documents}
							textValue={formState?.project_product_portfolio?.value}
							onCheckHandler={updateCheckHandler('project_product_portfolio')}
							checkValue={formState?.project_product_portfolio?.show_in_cran}
							onToggleChange={commentTypeHandler('project_product_portfolio')}
							onButtonClick={addHandler('project_product_portfolio')}
							disabled={formState?.project_product_portfolio?.disabled}
							probeId={probeId}
							commentKey={'project_product_portfolio'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.key_industries_clients_catered?.label}
							placeholder={formState?.key_industries_clients_catered?.placeholder}
							toggleState={formState?.key_industries_clients_catered?.comment_type}
							savedDocuments={formState?.key_industries_clients_catered?.documents}
							textValue={formState?.key_industries_clients_catered?.value}
							onCheckHandler={updateCheckHandler('key_industries_clients_catered')}
							checkValue={formState?.key_industries_clients_catered?.show_in_cran}
							onToggleChange={commentTypeHandler('key_industries_clients_catered')}
							onButtonClick={addHandler('key_industries_clients_catered')}
							disabled={formState?.key_industries_clients_catered?.disabled}
							probeId={probeId}
							commentKey={'key_industries_clients_catered'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.fund_based_credit_rating?.label}
							placeholder={formState?.fund_based_credit_rating?.placeholder}
							toggleState={formState?.fund_based_credit_rating?.comment_type}
							savedDocuments={formState?.fund_based_credit_rating?.documents}
							textValue={formState?.fund_based_credit_rating?.value}
							onCheckHandler={updateCheckHandler('fund_based_credit_rating')}
							checkValue={formState?.fund_based_credit_rating?.show_in_cran}
							onToggleChange={commentTypeHandler('fund_based_credit_rating')}
							onButtonClick={addHandler('fund_based_credit_rating')}
							disabled={formState?.fund_based_credit_rating?.disabled}
							probeId={probeId}
							commentKey={'fund_based_credit_rating'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.non_fund_based_credit_rating?.label}
							placeholder={formState?.non_fund_based_credit_rating?.placeholder}
							toggleState={formState?.non_fund_based_credit_rating?.comment_type}
							savedDocuments={formState?.non_fund_based_credit_rating?.documents}
							textValue={formState?.non_fund_based_credit_rating?.value}
							onCheckHandler={updateCheckHandler('non_fund_based_credit_rating')}
							checkValue={formState?.non_fund_based_credit_rating?.show_in_cran}
							onToggleChange={commentTypeHandler('non_fund_based_credit_rating')}
							onButtonClick={addHandler('non_fund_based_credit_rating')}
							disabled={formState?.non_fund_based_credit_rating?.disabled}
							probeId={probeId}
							commentKey={'non_fund_based_credit_rating'}
						/>
					</FieldWrapper>
				</>
			)
		else return null
	}

	const getBusinessRiskComments = () => {
		if (selectedTab === 1) {
			return (
				<>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.competitive_position?.label}
							placeholder={formState?.competitive_position?.placeholder}
							toggleState={formState?.competitive_position?.comment_type}
							savedDocuments={formState?.competitive_position?.documents}
							textValue={formState?.competitive_position?.value}
							onCheckHandler={updateCheckHandler('competitive_position')}
							checkValue={formState?.competitive_position?.show_in_cran}
							onToggleChange={commentTypeHandler('competitive_position')}
							onButtonClick={addHandler('competitive_position')}
							disabled={formState?.competitive_position?.disabled}
							probeId={probeId}
							commentKey={'competitive_position'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.diversification?.label}
							placeholder={formState?.diversification?.placeholder}
							toggleState={formState?.diversification?.comment_type}
							savedDocuments={formState?.diversification?.documents}
							textValue={formState?.diversification?.value}
							onCheckHandler={updateCheckHandler('diversification')}
							checkValue={formState?.diversification?.show_in_cran}
							onToggleChange={commentTypeHandler('diversification')}
							onButtonClick={addHandler('diversification')}
							disabled={formState?.diversification?.disabled}
							probeId={probeId}
							commentKey={'diversification'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.assets_quality?.label}
							placeholder={formState?.assets_quality?.placeholder}
							toggleState={formState?.assets_quality?.comment_type}
							savedDocuments={formState?.assets_quality?.documents}
							textValue={formState?.assets_quality?.value}
							onCheckHandler={updateCheckHandler('assets_quality')}
							checkValue={formState?.assets_quality?.show_in_cran}
							onToggleChange={commentTypeHandler('assets_quality')}
							onButtonClick={addHandler('assets_quality')}
							disabled={formState?.assets_quality?.disabled}
							probeId={probeId}
							commentKey={'assets_quality'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.project_risk?.label}
							placeholder={formState?.project_risk?.placeholder}
							toggleState={formState?.project_risk?.comment_type}
							savedDocuments={formState?.project_risk?.documents}
							textValue={formState?.project_risk?.value}
							onCheckHandler={updateCheckHandler('project_risk')}
							checkValue={formState?.project_risk?.show_in_cran}
							onToggleChange={commentTypeHandler('project_risk')}
							onButtonClick={addHandler('project_risk')}
							disabled={formState?.project_risk?.disabled}
							probeId={probeId}
							commentKey={'project_risk'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.industry_diversification?.label}
							placeholder={formState?.industry_diversification?.placeholder}
							toggleState={formState?.industry_diversification?.comment_type}
							savedDocuments={formState?.industry_diversification?.documents}
							textValue={formState?.industry_diversification?.value}
							onCheckHandler={updateCheckHandler('industry_diversification')}
							checkValue={formState?.industry_diversification?.show_in_cran}
							onToggleChange={commentTypeHandler('industry_diversification')}
							onButtonClick={addHandler('industry_diversification')}
							disabled={formState?.industry_diversification?.disabled}
							probeId={probeId}
							commentKey={'industry_diversification'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.parent_and_group_companies?.label}
							placeholder={formState?.parent_and_group_companies?.placeholder}
							toggleState={formState?.parent_and_group_companies?.comment_type}
							savedDocuments={formState?.parent_and_group_companies?.documents}
							textValue={formState?.parent_and_group_companies?.value}
							onCheckHandler={updateCheckHandler('parent_and_group_companies')}
							checkValue={formState?.parent_and_group_companies?.show_in_cran}
							onToggleChange={commentTypeHandler('parent_and_group_companies')}
							onButtonClick={addHandler('parent_and_group_companies')}
							disabled={formState?.parent_and_group_companies?.disabled}
							probeId={probeId}
							commentKey={'parent_and_group_companies'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.support_level?.label}
							placeholder={formState?.support_level?.placeholder}
							toggleState={formState?.support_level?.comment_type}
							savedDocuments={formState?.support_level?.documents}
							textValue={formState?.support_level?.value}
							onCheckHandler={updateCheckHandler('support_level')}
							checkValue={formState?.support_level?.show_in_cran}
							onToggleChange={commentTypeHandler('support_level')}
							onButtonClick={addHandler('support_level')}
							disabled={formState?.support_level?.disabled}
							probeId={probeId}
							commentKey={'support_level'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.strategic?.label}
							placeholder={formState?.strategic?.placeholder}
							toggleState={formState?.strategic?.comment_type}
							savedDocuments={formState?.strategic?.documents}
							textValue={formState?.strategic?.value}
							onCheckHandler={updateCheckHandler('strategic')}
							checkValue={formState?.strategic?.show_in_cran}
							onToggleChange={commentTypeHandler('strategic')}
							onButtonClick={addHandler('strategic')}
							disabled={formState?.strategic?.disabled}
							probeId={probeId}
							commentKey={'strategic'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.business?.label}
							placeholder={formState?.business?.placeholder}
							toggleState={formState?.business?.comment_type}
							savedDocuments={formState?.business?.documents}
							textValue={formState?.business?.value}
							onCheckHandler={updateCheckHandler('business')}
							checkValue={formState?.business?.show_in_cran}
							onToggleChange={commentTypeHandler('business')}
							onButtonClick={addHandler('business')}
							disabled={formState?.business?.disabled}
							probeId={probeId}
							commentKey={'business'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.present_bankers?.label}
							placeholder={formState?.present_bankers?.placeholder}
							toggleState={formState?.present_bankers?.comment_type}
							savedDocuments={formState?.present_bankers?.documents}
							textValue={formState?.present_bankers?.value}
							onCheckHandler={updateCheckHandler('present_bankers')}
							checkValue={formState?.present_bankers?.show_in_cran}
							onToggleChange={commentTypeHandler('present_bankers')}
							onButtonClick={addHandler('present_bankers')}
							disabled={formState?.present_bankers?.disabled}
							probeId={probeId}
							commentKey={'present_bankers'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.market_conduct?.label}
							placeholder={formState?.market_conduct?.placeholder}
							toggleState={formState?.market_conduct?.comment_type}
							savedDocuments={formState?.market_conduct?.documents}
							textValue={formState?.market_conduct?.value}
							onCheckHandler={updateCheckHandler('market_conduct')}
							checkValue={formState?.market_conduct?.show_in_cran}
							onToggleChange={commentTypeHandler('market_conduct')}
							onButtonClick={addHandler('market_conduct')}
							disabled={formState?.market_conduct?.disabled}
							probeId={probeId}
							commentKey={'market_conduct'}
						/>
					</FieldWrapper>
				</>
			)
		}
		return null
	}

	const getIndustryRiskComments = () => {
		if (selectedTab === 2) {
			return (
				<>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.growth_prospects?.label}
							placeholder={formState?.growth_prospects?.placeholder}
							toggleState={formState?.growth_prospects?.comment_type}
							savedDocuments={formState?.growth_prospects?.documents}
							textValue={formState?.growth_prospects?.value}
							onCheckHandler={updateCheckHandler('growth_prospects')}
							checkValue={formState?.growth_prospects?.show_in_cran}
							onToggleChange={commentTypeHandler('growth_prospects')}
							onButtonClick={addHandler('growth_prospects')}
							disabled={formState?.growth_prospects?.disabled}
							probeId={probeId}
							commentKey={'growth_prospects'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.cyclicality?.label}
							placeholder={formState?.cyclicality?.placeholder}
							toggleState={formState?.cyclicality?.comment_type}
							savedDocuments={formState?.cyclicality?.documents}
							textValue={formState?.cyclicality?.value}
							onCheckHandler={updateCheckHandler('cyclicality')}
							checkValue={formState?.cyclicality?.show_in_cran}
							onToggleChange={commentTypeHandler('cyclicality')}
							onButtonClick={addHandler('cyclicality')}
							disabled={formState?.cyclicality?.disabled}
							probeId={probeId}
							commentKey={'cyclicality'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.competitive_intensity?.label}
							placeholder={formState?.competitive_intensity?.placeholder}
							toggleState={formState?.competitive_intensity?.comment_type}
							savedDocuments={formState?.competitive_intensity?.documents}
							textValue={formState?.competitive_intensity?.value}
							onCheckHandler={updateCheckHandler('competitive_intensity')}
							checkValue={formState?.competitive_intensity?.show_in_cran}
							onToggleChange={commentTypeHandler('competitive_intensity')}
							onButtonClick={addHandler('competitive_intensity')}
							disabled={formState?.competitive_intensity?.disabled}
							probeId={probeId}
							commentKey={'competitive_intensity'}
						/>
					</FieldWrapper>
					<FieldWrapper>
						<CranCommentCard
							label={formState?.regulatory_risk?.label}
							placeholder={formState?.regulatory_risk?.placeholder}
							toggleState={formState?.regulatory_risk?.comment_type}
							savedDocuments={formState?.regulatory_risk?.documents}
							textValue={formState?.regulatory_risk?.value}
							onCheckHandler={updateCheckHandler('regulatory_risk')}
							checkValue={formState?.regulatory_risk?.show_in_cran}
							onToggleChange={commentTypeHandler('regulatory_risk')}
							onButtonClick={addHandler('regulatory_risk')}
							disabled={formState?.regulatory_risk?.disabled}
							probeId={probeId}
							commentKey={'regulatory_risk'}
						/>
					</FieldWrapper>
				</>
			)
		}
		return null
	}

	return (
		<>
			<DrawerBody>
				<DrawerNavigationWrapper>
					<Tabs fixed data={tabs} />
				</DrawerNavigationWrapper>
				<CommentsSection>
					{getBriefComments()}
					{getBusinessRiskComments()}
					{getIndustryRiskComments()}
				</CommentsSection>
			</DrawerBody>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default CompanyRiskForm
